import { useCurrentUserCtx } from 'client/contexts/CurrentUser';
import AnchorAPI from 'client/modules/AnchorAPI';
import { Metadata } from 'client/types/Metadata';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const USE_FETCH_PODCAST_METADATA_KEY = 'useFetchPodcastMetadata';

function useFetchPodcastMetadata(fetchOnMount = true) {
  const {
    state: { webStationId },
  } = useCurrentUserCtx();
  const { data, status, isError, refetch } = useQuery(
    [USE_FETCH_PODCAST_METADATA_KEY, webStationId],
    () => AnchorAPI.fetchPodcastMetadata({ webStationId: webStationId! }),
    {
      enabled: !!webStationId && fetchOnMount,
      // setting the stale time to 0 since there are many actions that could
      // change a podcast's metadata and it might be unweildy to try to invalidate
      // all the different spots where we can update metaddata
      staleTime: 0,
    }
  );
  return { data, status, isError, refetch };
}

function useUpdatePodcastMetadta() {
  const queryClient = useQueryClient();
  const {
    state: { webStationId, userId },
  } = useCurrentUserCtx();
  const { mutate, status } = useMutation(
    (metadata: Metadata) => {
      if (!webStationId) {
        throw new Error(
          'Unable to update podcast metadata. webStationId is not defined'
        );
      }
      if (!userId) {
        throw new Error(
          'Unable to update podcast metadata. userId is not defined'
        );
      }
      return AnchorAPI.updatePodcastMetadata({
        webStationId,
        userId,
        metadata,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          USE_FETCH_PODCAST_METADATA_KEY,
          webStationId,
        ]);
      },
    }
  );

  return { mutate, status };
}

export { useFetchPodcastMetadata, useUpdatePodcastMetadta };
